import { useState, useRef } from 'react';
import { Button, Icon, TwModal as Modal, Text, TwButton } from '@ui-component-library';
import { kycPreferences } from '../../../api/services/user';
import Cookies from 'js-cookie';

import { KYCStep1 } from './KYCStep1';
import { KYCStep2 } from './KYCStep2';
import { KYCStep3 } from './KYCStep3';
import { KYCStep4 } from './KYCStep4';
import { KYCDone } from './KYCDone';
import { KYCSkip } from './KYCSkip';
import { useUserDataStore } from '../store/UserDataStore';
import type { KYCFormData, KYCModalProps, KYCStepCodes } from './KYC.types';
import { KYCStartPage } from './KYCStartPage';

export const KYCModal = ({ properties = [], onComplete }: KYCModalProps) => {
  const { setKyc } = useUserDataStore();
  const [currentStep, setCurrentStep] = useState(1);
  const hasProperties = (properties ?? []).length > 0;
  const stepWithProps = hasProperties ? [1, 2, 3] : [1, 2, 3, 4];
  const totalSteps = stepWithProps.length + 1;
  const percentage = Math.round((currentStep / totalSteps) * 100);
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skipKYCModal, setSkipKYCModal] = useState(false);
  const [startPageKYCModal, setStartPageKYCModal] = useState(true)
  const [submitError, setSubmitError] = useState(false);
  const [formData, setFormData] = useState<KYCFormData>({
    selected: [],
    selectedPropertyRefs: [],
    selectedPropertiesFromSearch: [],
  });
  const contentTopRef = useRef<HTMLDivElement>(null);

  const stepCodes: KYCStepCodes = {
    1: ['virkespriserLonsamhet', 'fastighetsvarde', 'effektivForvaltning', 'jaktViltvard', 'rekreationFriluftsliv', 'klimatKolkrediter', 'biologiskMangfald', 'forebyggaSkador'],
    2: ['jamforVirkespriser', 'digitalSkogsbruksplan', 'kollaPaMinSkog', 'seFastighetsvarde', 'ekonomiKassaflode', 'skadeinventeringDrone', 'radgivare', 'kolkrediter', 'saljaVirke', 'saljaBiobransle'],
    3: ['borPaSkogsfastighet', 'borIntePaSkogsfastighet', 'agerIngenSkog'],
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(false);
    const { selectedPropertyRefs, selectedPropertiesFromSearch, ...kycData } = formData;
    // Send only the selected array - backend ignores individual boolean fields
    const response = await kycPreferences({ selected: kycData.selected || [] });
    setIsSubmitting(false);
    if (response.ok) {
      setOpenModal(false);
      setKyc({ hasDismissedKyc: false, hasCompletedKyc: true });
      onComplete?.(formData);
    } else {
      console.error('Failed to submit KYC preferences');
      setSubmitError(true);
    }
  }

  const handleSkip = async () => {
    Cookies.set('kyc_dismissed_temp', 'true', { expires: 1 });

    const response = await kycPreferences({ hasDismissedKyc: true });
    if (response.ok) {
      setOpenModal(false);
      setKyc({ hasDismissedKyc: true, hasCompletedKyc: false });
    } else {
      console.error('Failed to dismiss KYC');
    }
  };

  const updateFormData = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const mergeFormData = (patch: (prev: KYCFormData) => Partial<KYCFormData>) => {
    setFormData((prev) => ({ ...prev, ...patch(prev) }));
  };

  const handleToggle = (code: string) => {
    const current = formData.selected || [];
    const newSelected = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code];
    updateFormData('selected', newSelected);
  };

  const isSelected = (code: string) => {
    return (formData.selected || []).includes(code);
  };

  const handleRadioGroup = (groupCodes: string[], selectedCode: string) => {
    // Remove all codes in the group, then add the selected one
    const current = formData.selected || [];
    const filtered = current.filter(code => !groupCodes.includes(code));
    updateFormData('selected', [...filtered, selectedCode]);
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      contentTopRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      contentTopRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  };

  const getButtonText = () => {
    if (currentStep === totalSteps) {
      return 'Till Min Skog';
    }
    return 'Nästa';
  };

  const disableNext = () => {
    if (currentStep === totalSteps) return false;
    const visualStep = currentStep;
    const selected = formData.selected || [];
    switch (visualStep) {
      case 1:
      case 2:
      case 3:
        return !stepCodes[visualStep].some((code) => selected.includes(code));
      default:
        return false;
    }
  };

  const renderStep = () => {
    if (currentStep === totalSteps) {
      return (
        <KYCDone
          isSubmitting={isSubmitting}
          hasError={submitError}
          onRetry={handleSubmit}
          onSkip={handleSkip}
        />
      )
    }
    const visualStep = currentStep;

    switch (visualStep) {
      case 1:
        return <KYCStep1 handleToggle={handleToggle} isSelected={isSelected} />;
      case 2:
        return <KYCStep2 handleToggle={handleToggle} isSelected={isSelected} />;
      case 3:
        return <KYCStep3 handleRadioGroup={handleRadioGroup} isSelected={isSelected} />;
      case 4:
        return (
          <KYCStep4
            properties={properties}
            formData={formData}
            mergeFormData={mergeFormData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal isOpen={openModal}>
        <Modal.Header>
          <div className="relative flex w-full items-center justify-between min-h-[38px]">
            <div className="w-[38px] min-w-[38px] shrink-0 flex items-center justify-center">
              {currentStep > 1 && currentStep < totalSteps ? (
                <TwButton
                  variant="tertiary"
                  size="icon"
                  onClick={handlePrevious}
                >
                  <Icon name="ArrowRightSimple" size="large" className="rotate-180" />
                </TwButton>
              ) : null}
            </div>
            <div className="text-center px-4">
              <Text variant="text-base/leading-normal/medium">
                Anpassa din upplevelse
              </Text>
            </div>
            <div className="w-[38px] min-w-[38px] shrink-0 flex items-center justify-center">
              {currentStep !== totalSteps ? (
                <TwButton
                  variant="tertiary"
                  size="icon"
                  onClick={() => setSkipKYCModal(true)}
                >
                  <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
                </TwButton>
              ) : null}
            </div>
          </div>
        </Modal.Header>
        <div className="bg-sandstone-light px-4 py-3 md:px-6">
          <div className="mb-2 flex items-center justify-between">
            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
              Steg {currentStep} av {totalSteps}
            </Text>
          </div>
          <div className="bg-charcoal-alpha-50 h-2 w-full overflow-hidden rounded-lg">
            <div
              className="h-full rounded-lg bg-[#349269] transition-all duration-300 ease-in-out"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
        <Modal.Content className="bg-sandstone-light">
        <div ref={contentTopRef} />{renderStep()}</Modal.Content>
        <Modal.Footer>
          <Button variant="primary" className="w-full" onClick={currentStep === totalSteps ? handleSubmit : handleNext} disabled={disableNext()}>
            {getButtonText()}
          </Button>
        </Modal.Footer>
      </Modal>
      <KYCStartPage 
        setStartPageKYCModal={setStartPageKYCModal} 
        startPageKYCModal={startPageKYCModal} 
        setOpenModal={setOpenModal}
      />
      <KYCSkip
        setSkipKYCModal={setSkipKYCModal}
        skipKYCModal={skipKYCModal}
        setOpenModal={setOpenModal}
      />
    </>
  );
};
